import { useEffect, useMemo, useState } from "react";
import {
  Crown,
  RefreshCw,
  RotateCcw,
  Search,
  SlidersHorizontal,
  UserRound,
  UsersRound,
  VenusAndMars,
} from "lucide-react";
import MemberStatCard from "../components/Member/MemberStatCard";
import MemberTable from "../components/Member/MemberTable";
import GenderPie from "../components/Member/GenderPie";
import AgeBar from "../components/Member/AgeBar";

const defaultFilters = { sex: "", isvip: "", age_group: "", search: "" };
const fieldClass = "h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-[#00b3b4] focus:ring-4 focus:ring-teal-500/10";

export default function Member() {
  const [members, setMembers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [chart, setChart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState(defaultFilters);

  const fetchData = async ({ silent = false, filterValues = filters } = {}) => {
    silent ? setRefreshing(true) : setLoading(true);
    setError("");
    const token = localStorage.getItem("token");
    const params = new URLSearchParams();
    Object.entries(filterValues).forEach(([key, value]) => value && params.append(key, value));

    try {
      const response = await fetch(`/api/member?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error("ไม่สามารถโหลดข้อมูลสมาชิกได้");
      const json = await response.json();
      setMembers(json.data || []);
      setSummary(json.summary || {});
      setChart(json.chart || {});
    } catch (fetchError) {
      setError(fetchError.message || "เกิดข้อผิดพลาด กรุณาลองอีกครั้ง");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      window.location.href = "/login";
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetFilter = () => {
    setFilters(defaultFilters);
    fetchData({ filterValues: defaultFilters });
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const vipRate = summary?.total ? Math.round((Number(summary.vip || 0) / Number(summary.total)) * 100) : 0;
  const dominantAge = useMemo(() => (chart?.age || []).reduce((top, item) => Number(item.count) > Number(top?.count || 0) ? item : top, null), [chart]);

  if (loading) {
    return <div className="min-h-screen bg-slate-50 p-5 md:p-8"><div className="mx-auto max-w-[1600px] animate-pulse space-y-6"><div className="h-20 rounded-2xl bg-slate-200" /><div className="h-36 rounded-2xl bg-white" /><div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{[1, 2, 3, 4].map((item) => <div key={item} className="h-28 rounded-2xl bg-white" />)}</div><div className="grid gap-6 xl:grid-cols-2"><div className="h-96 rounded-2xl bg-white" /><div className="h-96 rounded-2xl bg-white" /></div></div></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-6">
        <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div><div className="mb-2 flex items-center gap-2 text-sm font-medium text-teal-700"><span className="h-2 w-2 rounded-full bg-teal-500" /> Customer directory</div><h1 className="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">จัดการข้อมูลสมาชิก</h1><p className="mt-1 text-sm text-slate-500">ค้นหา วิเคราะห์ และเข้าถึงข้อมูลสมาชิกทั้งหมดในที่เดียว</p></div>
          <button onClick={() => fetchData({ silent: true })} disabled={refreshing} className="flex h-11 items-center justify-center gap-2 self-start rounded-xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 shadow-sm transition hover:bg-slate-50 disabled:opacity-60 sm:self-auto"><RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />{refreshing ? "กำลังอัปเดต" : "อัปเดตข้อมูล"}</button>
        </header>

        <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm md:p-5">
          <div className="mb-4 flex items-center justify-between"><div className="flex items-center gap-2"><SlidersHorizontal size={17} className="text-[#00b3b4]" /><h2 className="font-semibold text-slate-800">ค้นหาและกรองสมาชิก</h2></div>{activeFilterCount > 0 && <span className="rounded-full bg-teal-50 px-2.5 py-1 text-xs font-medium text-teal-700">ใช้งาน {activeFilterCount} ตัวกรอง</span>}</div>
          <form onSubmit={(event) => { event.preventDefault(); fetchData(); }} className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-[2fr_1fr_1fr_1fr_auto]">
            <label className="space-y-1.5"><span className="text-xs font-medium text-slate-500">ค้นหาสมาชิก</span><div className="relative"><Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><input type="search" placeholder="ชื่อ เบอร์โทร หรือรหัสลูกค้า" value={filters.search} onChange={(event) => setFilters({ ...filters, search: event.target.value })} className={`${fieldClass} pl-9`} /></div></label>
            <label className="space-y-1.5"><span className="text-xs font-medium text-slate-500">เพศ</span><select value={filters.sex} onChange={(event) => setFilters({ ...filters, sex: event.target.value })} className={fieldClass}><option value="">ทุกเพศ</option><option value="1">ผู้ชาย</option><option value="0">ผู้หญิง</option></select></label>
            <label className="space-y-1.5"><span className="text-xs font-medium text-slate-500">ประเภทสมาชิก</span><select value={filters.isvip} onChange={(event) => setFilters({ ...filters, isvip: event.target.value })} className={fieldClass}><option value="">ทุกประเภท</option><option value="1">VIP</option><option value="0">สมาชิกทั่วไป</option><option value="2">พนักงาน</option></select></label>
            <label className="space-y-1.5"><span className="text-xs font-medium text-slate-500">ช่วงอายุ</span><select value={filters.age_group} onChange={(event) => setFilters({ ...filters, age_group: event.target.value })} className={fieldClass}><option value="">ทุกช่วงอายุ</option><option value="18-25">18–25 ปี</option><option value="26-35">26–35 ปี</option><option value="36-45">36–45 ปี</option><option value="46+">46 ปีขึ้นไป</option></select></label>
            <div className="flex items-end gap-2"><button type="submit" className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-[#00b3b4] px-5 text-sm font-medium text-white shadow-sm transition hover:bg-[#008f90] focus:ring-4 focus:ring-teal-500/20"><Search size={16} />ค้นหา</button><button type="button" onClick={resetFilter} title="ล้างตัวกรอง" className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-800"><RotateCcw size={16} /></button></div>
          </form>
        </section>

        {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

        <section className="grid grid-cols-2 gap-4 xl:grid-cols-4">
          <MemberStatCard title="สมาชิกทั้งหมด" value={summary?.total} icon={UsersRound} detail="ตามตัวกรองปัจจุบัน" />
          <MemberStatCard title="ผู้ชาย" value={summary?.male} color="blue" icon={UserRound} detail={`${summary?.total ? Math.round((summary.male / summary.total) * 100) : 0}% ของสมาชิก`} />
          <MemberStatCard title="ผู้หญิง" value={summary?.female} color="pink" icon={VenusAndMars} detail={`${summary?.total ? Math.round((summary.female / summary.total) * 100) : 0}% ของสมาชิก`} />
          <MemberStatCard title="สมาชิก VIP" value={summary?.vip} color="yellow" icon={Crown} detail={`${vipRate}% ของสมาชิก`} />
        </section>

        {chart && <section className="grid grid-cols-1 gap-6 xl:grid-cols-2"><GenderPie data={chart.gender} total={summary?.total} /><AgeBar data={chart.age} dominantAge={dominantAge} /></section>}

        <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
          <div className="flex flex-col gap-2 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between"><div><h2 className="font-semibold text-slate-900">รายชื่อสมาชิก</h2><p className="mt-0.5 text-xs text-slate-500">คลิกรหัสหรือแถวเพื่อเปิดดูข้อมูลสมาชิก</p></div><span className="self-start rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 sm:self-auto">{formatNumber(members.length)} คน</span></div>
          {refreshing ? <div className="h-1 overflow-hidden bg-teal-50"><div className="h-full w-1/3 animate-pulse rounded-full bg-teal-500" /></div> : null}
          <MemberTable data={members} />
        </section>
      </div>
    </div>
  );
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString("th-TH");
}
